import { Dialog, DialogContent, Grid, Typography, DialogActions } from "@mui/material";
import {
  useNotify,
  Form,
  ArrayInput,
  SimpleFormIterator,
  SelectInput,
  ReferenceInput,
  NumberInput,
  useRecordContext,
  required,
  minValue,
  FormDataConsumer,
  useCreateController,
  useGetList,
  BooleanInput,
  Button,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { ScoreComponentSource } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import ModalTitle, { withModalSx, modalCancelButtonSx, modalConfirmButtonSx } from "server/components/Modals/ModalTitle";
import IndexField from "./IndexField";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import CalculateIcon from "@mui/icons-material/Calculate";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useState } from "react";
import { CitizenTypeOptions, ScoreComponentSourceChoices } from "utils/choices";

// Съдържанието на диалога — монтира се само при отваряне, за да не хвърля
// useGetList заявка за всеки ред от списъка със специалности.
const ScoreFormulaDialog = ({ specialtyRecord, onClose }) => {
  const notify = useNotify();
  const [citizenshipId, setCitizenshipId] = useState(0);

  const endpoint = candidateRequests.specialityScoreFormulas(specialtyRecord.id);

  const { data: scoreFormulas } = useGetList(endpoint);

  const { save } = useCreateController({
    resource: endpoint,
    mutationMode: "pessimistic",
    redirect: false,
    mutationOptions: {
      onSuccess: () => {
        notify("Формулата е запазена!", { type: "success" });
      },
    },
    transform: data => ({
      ...data,
      lines: data.lines.map(line => ({
        ...line,
        scoreFormulaId: data.Id,
        components: line.components.map(component => ({
          ...component,
          scoreFormulaLineId: line.Id,
        })),
      })),
    }),
  });

  const canManage = checkPermission(getPermissions(), permissions.canManageSpecialitiesScoreFormulas);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={withModalSx()}
    >
      <ModalTitle
        icon={<CalculateIcon />}
        title={`Формула за бал - специалност ${specialtyRecord.nameBg}`}
      />
      <Form
        onSubmit={save}
        toolbar={false}
        record={
          scoreFormulas?.find(formula => formula.citizenshipId === citizenshipId) || {
            id: 0,
            citizenshipId: citizenshipId,
            manualComponentSelection: false,
            lines: [{}],
          }
        }
      >
        <DialogContent dividers>
          <Typography
            variant="h6"
            align="center"
            letterSpacing={3}
            fontWeight="bold"
            sx={{ textTransform: "uppercase" }}
          >
            Гражданство
          </Typography>
          <Tabs
            value={citizenshipId}
            onChange={(_, value) => setCitizenshipId(value)}
            textColor="secondary"
            indicatorColor="secondary"
            aria-label="secondary tabs example"
            variant="fullWidth"
            allowScrollButtonsMobile
          >
            <Tab
              value={0}
              label="По подразбиране"
            />
            {CitizenTypeOptions.map(option => (
              <Tab
                value={option.id}
                label={option.name}
                key={option.id}
              />
            ))}
          </Tabs>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
            >
              <BooleanInput
                source="manualComponentSelection"
                label="Ръчно избиране на оценка от изпит"
              />
              <ArrayInput
                source="lines"
                label="Формула"
              >
                <SimpleFormIterator
                  disableReordering
                  disableAdd={!canManage}
                  disableRemove={!canManage}
                  disableClear={!canManage}
                  inline
                  sx={{
                    "& .RaSimpleFormIterator-line": {
                      paddingY: 1,
                      paddingX: 2,
                      marginBottom: 3,
                    },
                  }}
                >
                  <IndexField />
                  <ArrayInput
                    source="components"
                    label="Компоненти"
                  >
                    <SimpleFormIterator
                      disableReordering
                      disableAdd={!canManage}
                      disableRemove={!canManage}
                      disableClear={!canManage}
                      inline
                    >
                      <SelectInput
                        source="source"
                        label="Тип"
                        validate={required()}
                        helperText={false}
                        choices={ScoreComponentSourceChoices}
                        defaultValue={ScoreComponentSource.Exam}
                      />
                      <FormDataConsumer>
                        {({ scopedFormData }) => {
                          const isExam = scopedFormData.source === ScoreComponentSource.Exam;
                          const resource = isExam
                            ? administrationRequests.getExamsSelect()
                            : candidateRequests.getSchoolSubjectsSelect();
                          const label = isExam ? "Изпит" : "Училищен предмет";
                          const optionText = isExam ? "name" : "nameBg";

                          return (
                            <ReferenceInput
                              source="sourceId"
                              resource={resource}
                              reference={resource}
                            >
                              <SelectInput
                                disabled={!canManage}
                                optionText={optionText}
                                label={label}
                                helperText={false}
                                validate={required()}
                              />
                            </ReferenceInput>
                          );
                        }}
                      </FormDataConsumer>
                      <NumberInput
                        disabled={!canManage}
                        source="multiplier"
                        label="Множител"
                        helperText={false}
                        defaultValue={1}
                        validate={minValue(0.01)}
                      />
                    </SimpleFormIterator>
                  </ArrayInput>
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1.5rem",
            gap: 2,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            color="error"
            sx={modalCancelButtonSx}
            size="medium"
            startIcon={<CloseIcon />}
            label="Отказ"
          />
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={modalConfirmButtonSx}
            size="medium"
            startIcon={<SaveIcon />}
            label="Запази"
          />
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const ScoreFormulaForm = ({ label = "Формула за бал" }) => {
  const specialtyRecord = useRecordContext();
  const [open, setOpen] = useState(false);

  if (!specialtyRecord) return null;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        label={label}
      >
        <CalculateIcon />
      </Button>
      {open && (
        <ScoreFormulaDialog
          specialtyRecord={specialtyRecord}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default ScoreFormulaForm;
